/**
 * Music DNA screen state.
 *
 * Not one of the §4 product domains — it is feature state, like createStore. It
 * lives in a store rather than in the route component for one behavioural reason:
 * in the monolith every screen shared a single component instance, so an analysis
 * survived navigating away and back. Component-local useState would silently
 * throw the result away on the first sidebar click.
 *
 * SIMULATED. `analyze()` hashes the URL; no audio is fetched or analysed. What the store
 * does do for real is detect that a pasted link is from Spotify and carry that into the
 * request — see generationApi.musicDnaSupport() for the boundary and what it is still
 * missing.
 *
 * Both long operations here hold a processing state for a minimum of PROCESSING_MS
 * (lib/processing.ts). The distinction between them is worth keeping straight, and each
 * is documented at its own method: `analyze` awaits a genuine request and the floor only
 * stretches how long that wait is watched, whereas `regenerate` has nothing to wait for
 * at all and its floor is purely a UX state.
 */
import { create } from 'zustand';
import { apiErrorMessage, isPaymentRequired } from '../api/errors';
import { generationApi } from '../api/generationApi';
import { USE_MOCK_API } from '../config';
import { MUSIC_DNA_ANALYSIS_CREDITS } from '../lib/credits';
import { buildDnaPrompt, detectMusicSource } from '../lib/music';
import { processingDelay, withProcessingFloor } from '../lib/processing';
import type { DnaDirection, MusicDNA } from '../types/domain';
import { useCreditsStore } from './creditsStore';
import { useUiStore } from './uiStore';

/** The three creative directions, in the prototype's order. */
const DIRECTION_LABELS = [
  'Faithful interpretation',
  'Bold reimagining',
  'Minimalist take',
] as const;

/**
 * Shown when the pasted link is not a supported Spotify song link.
 */
export const INVALID_URL_ERROR = 'Paste a valid Spotify song link (open.spotify.com/track/…)';

interface DnaState {
  url: string;
  analyzing: boolean;
  /**
   * True while a reroll's processing state is showing.
   *
   * Separate from `analyzing` rather than reusing it, because they gate different
   * things: `analyzing` disables Analyze and hides the dashboard, while this one leaves
   * the analysis on screen — it is the prompt being rewritten, not the profile being
   * replaced — and only stands in for the composer.
   */
  regenerating: boolean;
  /** Validation message under the input, or '' when clear. */
  error: string;
  /** The analysed profile, or null before the first analysis. */
  dna: MusicDNA | null;
  directions: DnaDirection[];
  /** Index into `directions`. */
  selectedDirection: number;
  /** The editable prompt. Diverges from the direction once hand-edited. */
  prompt: string;
  /** 0–100 inspiration-strength slider. */
  inspirationStrength: number;
  /** Reroll counter, so "Regenerate" produces different phrasing. */
  variant: number;

  setUrl: (url: string) => void;
  setPrompt: (prompt: string) => void;
  setInspirationStrength: (strength: number) => void;
  /**
   * Analyses the link and builds the three directions.
   *
   * Returns false without touching state when the URL is rejected, so the caller
   * knows not to show a success toast.
   */
  analyze: () => Promise<boolean>;
  selectDirection: (index: number) => void;
  /**
   * Rerolls the selected direction's prompt in place, behind a processing state.
   *
   * Async now, where it used to be synchronous and instant. Nothing about the reroll got
   * slower — buildDnaPrompt is a string builder over data already held — so the five
   * seconds are a UX state and only that, which is the whole of what the product asked
   * for here. The new text is written when the wait ends rather than before it, because
   * revealing the result and then holding a "working" notice over it would be a lie
   * about what the user is waiting for.
   */
  regeneratePrompt: () => Promise<void>;
  reset: () => void;
}

const INITIAL = {
  url: '',
  analyzing: false,
  regenerating: false,
  error: '',
  dna: null,
  directions: [] as DnaDirection[],
  selectedDirection: 0,
  prompt: '',
  inspirationStrength: 60,
  variant: 0,
};

export const useDnaStore = create<DnaState>((set, get) => ({
  ...INITIAL,

  setUrl(url) {
    set({ url });
  },

  setPrompt(prompt) {
    set({ prompt });
  },

  setInspirationStrength(inspirationStrength) {
    set({ inspirationStrength });
  },

  async analyze() {
    set({ error: '' });
    /*
      One check names the accepted service, and the source it produces is carried into the
      request rather than thrown away. A store that only asked "is this acceptable?" would
      leave the API boundary to re-derive the answer from the string.
    */
    const source = detectMusicSource(get().url);
    if (!source) {
      set({ error: INVALID_URL_ERROR });
      return false;
    }

    set({ analyzing: true });
    try {
      if (USE_MOCK_API && !useCreditsStore.getState().canAfford(MUSIC_DNA_ANALYSIS_CREDITS)) {
        useUiStore.getState().setOutOfCreditsOpen(true);
        return false;
      }

      /*
        The analysis is held on screen for a minimum of PROCESSING_MS.

        The request is real (as real as a mock gets — it resolves in 1300ms), and it is
        already in flight when the floor starts: both are awaited and the later decides,
        so this stretches how long a genuine wait is watched and nothing else. It does not
        fabricate a result, delay one that exists, or hold back a failure — a rejection
        propagates the moment it happens. See lib/processing.ts.
      */
      const dna = await withProcessingFloor(generationApi.analyzeMusicDna(get().url, source));
      if (USE_MOCK_API) {
        const charged = await useCreditsStore
          .getState()
          .spend(MUSIC_DNA_ANALYSIS_CREDITS, 'Music DNA analysis');
        if (!charged) {
          useUiStore.getState().setOutOfCreditsOpen(true);
          return false;
        }
      } else {
        // The backend committed the reservation only after returning valid DNA.
        await useCreditsStore.getState().loadBalance();
      }
      const strength = get().inspirationStrength;
      const directions: DnaDirection[] = DIRECTION_LABELS.map((label) => ({
        label,
        prompt: buildDnaPrompt(dna, label, strength, 0),
      }));
      set({
        dna,
        directions,
        selectedDirection: 0,
        variant: 0,
        prompt: directions[0].prompt,
      });
      return true;
    } catch (err) {
      if (isPaymentRequired(err)) {
        useUiStore.getState().setOutOfCreditsOpen(true);
        return false;
      }
      set({ error: apiErrorMessage(err, { preserveMessage: true }) });
      return false;
    } finally {
      // In `finally` so a rejected analysis cannot leave the button disabled.
      set({ analyzing: false });
    }
  },

  selectDirection(index) {
    const direction = get().directions[index];
    if (!direction) return;
    set({ selectedDirection: index, prompt: direction.prompt });
  },

  async regeneratePrompt() {
    if (!get().dna) return;

    set({ regenerating: true });
    try {
      // processingDelay, not withProcessingFloor: there is no request to run alongside
      // it. Being explicit about that is the point — see the interface comment above.
      await processingDelay();

      // Read after the wait, not before. Five seconds is long enough for the user to
      // have switched direction or moved the strength slider, and rerolling the state
      // they left rather than the state they are looking at would be a bug the old
      // synchronous version could not have had.
      const { dna, directions, selectedDirection, inspirationStrength, variant } = get();
      if (!dna) return;
      const nextVariant = variant + 1;
      const refreshed = buildDnaPrompt(
        dna,
        DIRECTION_LABELS[selectedDirection],
        inspirationStrength,
        nextVariant
      );
      set({
        variant: nextVariant,
        prompt: refreshed,
        // The reroll is written back into the direction, so switching away and back
        // shows the regenerated text rather than reverting to the original.
        directions: directions.map((d, i) =>
          i === selectedDirection ? { ...d, prompt: refreshed } : d
        ),
      });
    } finally {
      // In `finally` for the same reason `analyzing` is: the button must not be left
      // disabled if anything above throws.
      set({ regenerating: false });
    }
  },

  reset() {
    set({ ...INITIAL, directions: [] });
  },
}));
